import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export type GenderValue = 'male' | 'female' | 'other' | 'unknown';

interface GenderOption {
  value: GenderValue;
  label: string;
  icon: string;
  description?: string;
}

const GENDER_OPTIONS: GenderOption[] = [
  { 
    value: 'male', 
    label: 'Male', 
    icon: '',
    description: 'Male'
  },
  { 
    value: 'female', 
    label: 'Female', 
    icon: '',
    description: 'Female'
  },
  { 
    value: 'other', 
    label: 'Other', 
    icon: '',
    description: 'Non-binary, gender-fluid, or other gender identity'
  },
  { 
    value: 'unknown', 
    label: 'Prefer not to say', 
    icon: '',
    description: 'Prefer not to disclose'
  },
];

interface GenderSelectProps {
  value?: GenderValue | '';
  onChange?: (value: GenderValue) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  placeholder?: string;
  showIcons?: boolean;
  showDescriptions?: boolean;
}

export const GenderSelect: React.FC<GenderSelectProps> = ({
  value = '',
  onChange,
  label = 'Gender',
  required = false,
  disabled = false,
  error,
  className = '',
  placeholder = 'Select gender',
  showIcons = true,
  showDescriptions = false,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value as GenderValue;
    if (selectedValue && onChange) {
      onChange(selectedValue);
    }
  };

  const getSelectClassName = () => {
    let baseClassName = `
      w-full px-3 py-2 pr-10 border rounded-md appearance-none
      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
      disabled:opacity-50 disabled:cursor-not-allowed
      transition-colors duration-200 bg-white
    `;

    if (error) {
      baseClassName += ' border-red-300 bg-red-50';
    } else {
      baseClassName += ' border-gray-300';
    }

    return baseClassName;
  };

  const selectedOption = GENDER_OPTIONS.find(option => option.value === value);

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <select
          value={value}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          className={getSelectClassName()}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {GENDER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {showIcons ? `${option.icon} ${option.label}` : option.label}
            </option>
          ))}
        </select>
        
        {/* Custom dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        </div>
      </div>
      
      {/* Selected option description */}
      {showDescriptions && selectedOption?.description && (
        <p className="text-sm text-gray-600">
          {selectedOption.description}
        </p>
      )}
      
      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600">
          {error}
        </p>
      )}
      
      {/* Help text */}
      {!error && !value && (
        <p className="text-xs text-gray-500">
          This information is used for medical record keeping and patient identification
        </p>
      )}
    </div>
  );
};